/**
 * Books to fix: billing's worklist (#1119). Pax8 is the source of truth for the licence, its SKU and its quantity, so
 * Books is corrected to match: quantity differences, Books items with the wrong or no Pax8 SKU, licence lines billed
 * with no Pax8 subscription, and Pax8 subscriptions with no Books line. Each row deep-links to the Books recurring
 * invoice (and the Books item when the API sends that link) and has "Mark done". A Books update card comes later.
 *
 * "Mark done" says "I fixed this in Books"; the row stays listed as done until the next reconciliation finds Books
 * matching and drops it (or reports it again). The route is `POST /licences/books-fix/done` (licences-recon #1127): the mark
 * is kept across rebuilds against the row's current fixes, so a changed problem shows as to-do again.
 */
import { Button, Chip, EmptyState, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import * as React from 'react'
import { describeError, unwrap } from '../../lib/api/client'
import {
  type BooksFixCode,
  type BooksFixItem,
  type BooksFixRowOut,
  markBooksFixDoneApiV1LicencesBooksFixDonePost,
  type Ok,
} from '../../lib/api/generated'
import { timeLabel } from '../profit/format'
import { QueryView } from '../profit/states'
import { booksFixQuery, FilterChips, LinkButton, licenceKeys, qty, SearchBox } from './common'
import { NotesButton } from './notes'

/** One Books-to-fix row (``done_at``/``done_by`` and ``links.books_item`` come from licences-recon, #1127). */
export type BooksFixRow = BooksFixRowOut

export const CODE_LABEL: Record<BooksFixCode, string> = {
  qty_mismatch: 'Quantity differs',
  wrong_sku: 'Wrong SKU',
  no_sku: 'No SKU',
  term_mismatch: 'Term differs',
  not_billed: 'Not billed',
  no_pax8_subscription: 'Not in Pax8',
}

/** The change to make, in Books' terms: "Books 29 → 31". */
export function changeLine(item: BooksFixItem): string | null {
  if (item.expected_quantity != null || item.books_quantity != null)
    return `Quantity: Books ${qty(item.books_quantity)} → ${qty(item.expected_quantity)}`
  if (item.expected_sku || item.books_sku) return `SKU: Books ${item.books_sku ?? 'none'} → ${item.expected_sku ?? 'none'}`
  return null
}

export function markBooksFixDone(lineKey: string): Promise<Ok> {
  return unwrap<Ok>(markBooksFixDoneApiV1LicencesBooksFixDonePost({ body: { line_key: lineKey } }))
}

type View = 'open' | 'done' | 'all'

function FixRow({ row, done, onDone, busy }: { row: BooksFixRow; done: boolean; onDone: () => void; busy: boolean }) {
  const title = row.product_name ?? 'Licence'
  const customer = row.books_customer_name ?? row.company_name ?? 'Unknown customer'
  return (
    <li
      data-testid="books-fix-row"
      data-line={row.line_key}
      data-done={done || undefined}
      className="grid gap-3 rounded-lg border border-border bg-card px-4 py-3 md:grid-cols-[minmax(12rem,1fr)_minmax(16rem,2fr)_auto] md:items-start"
    >
      <div className="min-w-0">
        <p className="font-medium">{customer}</p>
        <p className="text-sm text-muted-foreground">{title}</p>
        {row.company_name && row.books_customer_name && row.company_name !== row.books_customer_name ? (
          <p className="text-xs text-subtle">Pax8: {row.company_name}</p>
        ) : null}
      </div>
      <ul className="min-w-0 space-y-1.5" aria-label={`What to change for ${customer} ${title}`}>
        {row.items.map((item) => (
          <li key={`${item.code}-${item.detail}`} className="text-sm">
            <span className="mr-1.5 font-medium text-warning">{CODE_LABEL[item.code] ?? item.code}</span>
            <span>{item.detail}</span>
            {changeLine(item) ? <span className="block font-mono text-xs text-muted-foreground">{changeLine(item)}</span> : null}
          </li>
        ))}
        {row.billing_change_state === 'notified' ? (
          <li className="text-xs text-muted-foreground">Billing was emailed the exact line for a confirmed change.</li>
        ) : null}
      </ul>
      <div className="flex flex-wrap items-center gap-1.5 md:justify-end">
        <LinkButton href={row.links?.books_invoice}>Recurring invoice</LinkButton>
        <LinkButton href={row.links?.books_item}>Books item</LinkButton>
        <LinkButton href={row.links?.books_customer}>Books customer</LinkButton>
        <LinkButton href={row.links?.pax8}>Pax8</LinkButton>
        <NotesButton lineKey={row.line_key} count={row.notes_count ?? 0} title={`${customer} · ${title}`} subtitle="Books to fix" />
        {done ? (
          <Chip tone="success" title={row.done_at ? `Marked done ${timeLabel(row.done_at)}` : 'Marked done'}>
            <Check aria-hidden /> Done{row.done_by ? ` · ${row.done_by.name}` : ''}
          </Chip>
        ) : (
          <Button size="sm" variant="primary" disabled={busy} onClick={onDone} aria-label={`Mark done: ${customer} ${title}`}>
            Mark done
          </Button>
        )}
      </div>
    </li>
  )
}

export function BooksFixTab() {
  const q = useQuery(booksFixQuery())
  const queryClient = useQueryClient()
  const [view, setView] = React.useState<View>('open')
  const [code, setCode] = React.useState<BooksFixCode | 'any'>('any')
  const [search, setSearch] = React.useState('')
  // Done this session, before the refetch says so (the server's done_at is the record).
  const [markedHere, setMarkedHere] = React.useState<Set<string>>(() => new Set())
  const mark = useMutation({
    mutationFn: markBooksFixDone,
    onSuccess: async (_ok, lineKey) => {
      setMarkedHere((prev) => new Set(prev).add(lineKey))
      toast.success('Marked done. Tonight’s check confirms Books matches Pax8.')
      await queryClient.invalidateQueries({ queryKey: licenceKeys.booksFix })
    },
    onError: (error) => toast.error(`Not marked done: ${describeError(error)}`),
  })
  return (
    <QueryView query={q} what="Books to fix">
      {(data) => {
        if (!data.computed_at) return <EmptyState title="No reconciliation has run yet." />
        const rows = data.rows as BooksFixRow[]
        const isDone = (r: BooksFixRow) => Boolean(r.done_at) || r.billing_change_state === 'done' || markedHere.has(r.line_key)
        const inView = rows.filter((r) => (view === 'all' ? true : view === 'done' ? isDone(r) : !isDone(r)))
        const term = search.trim().toLowerCase()
        const shown = inView.filter(
          (r) =>
            (code === 'any' || r.items.some((i) => i.code === code)) &&
            (!term ||
              [r.company_name, r.books_customer_name, r.product_name, ...r.items.map((i) => i.detail)]
                .join(' ')
                .toLowerCase()
                .includes(term)),
        )
        const codes = Object.keys(CODE_LABEL) as BooksFixCode[]
        return (
          <div className="space-y-3.5">
            <p className="max-w-3xl text-sm text-muted-foreground">
              Pax8 is the source of truth for which licence and how many. Fix each line in Books, then mark it done; the nightly check
              confirms it and drops the row. Checked {timeLabel(data.computed_at)}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <FilterChips
                label="Worklist state"
                value={view}
                onChange={setView}
                options={[
                  { id: 'open', label: 'To do', count: rows.filter((r) => !isDone(r)).length },
                  { id: 'done', label: 'Marked done', count: rows.filter(isDone).length },
                  { id: 'all', label: 'All', count: rows.length },
                ]}
              />
              <FilterChips
                label="Fix kind"
                value={code}
                onChange={setCode}
                options={[
                  { id: 'any' as const, label: 'Any', count: inView.length },
                  ...codes
                    .map((c) => ({ id: c, label: CODE_LABEL[c], count: inView.filter((r) => r.items.some((i) => i.code === c)).length }))
                    .filter((o) => o.count > 0 || o.id === code),
                ]}
              />
            </div>
            <SearchBox value={search} onChange={setSearch} label="Search customer, licence" count={`${shown.length} of ${inView.length}`} />
            {shown.length === 0 ? (
              <EmptyState
                title={
                  view === 'open' && rows.length > 0 && !term && code === 'any'
                    ? 'Everything here is marked done.'
                    : 'Nothing to fix matches.'
                }
                detail={rows.length === 0 ? 'Books matches Pax8 on every mapped licence.' : undefined}
              />
            ) : (
              <ul aria-label="Books to fix" className="space-y-2">
                {shown.map((r) => (
                  <FixRow
                    key={r.line_key}
                    row={r}
                    done={isDone(r)}
                    busy={mark.isPending && mark.variables === r.line_key}
                    onDone={() => mark.mutate(r.line_key)}
                  />
                ))}
              </ul>
            )}
          </div>
        )
      }}
    </QueryView>
  )
}
